import { TouchEvent, useState } from 'react';

interface SwipeInput {
    onSwipedLeft?: () => void;
    onSwipedRight?: () => void;
    onSwipedUp?: () => void;
    onSwipedDown?: () => void;
}

interface SwipeOutput {
    onTouchStart: (e: TouchEvent) => void;
    onTouchMove: (e: TouchEvent) => void;
    onTouchEnd: () => void;
}

const useSwipe = (input: SwipeInput): SwipeOutput => {
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);

    // the required distance between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 50;

    const onTouchStart = (e: TouchEvent) => {
        setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
        setTouchStart(e.targetTouches[0].clientX);
        setTouchStartY(e.targetTouches[0].clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
        setTouchEndY(e.targetTouches[0].clientY);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distanceX = touchStart - touchEnd;
        const isLeftSwipe = distanceX > minSwipeDistance;
        const isRightSwipe = distanceX < -minSwipeDistance;

        // Check for vertical swipe to avoid triggering horizontal swipe when scrolling
        const distanceY = touchStartY && touchEndY ? touchStartY - touchEndY : 0;
        const isVerticalSwipe = Math.abs(distanceY) > Math.abs(distanceX);

        if (isVerticalSwipe) {
            if (distanceY > minSwipeDistance && input.onSwipedUp) {
                input.onSwipedUp();
            }
            if (distanceY < -minSwipeDistance && input.onSwipedDown) {
                input.onSwipedDown();
            }
            return;
        }

        if (isLeftSwipe && input.onSwipedLeft) {
            input.onSwipedLeft();
        }
        if (isRightSwipe && input.onSwipedRight) {
            input.onSwipedRight();
        }
    };

    return {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    };
};

export default useSwipe;
